import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getTopGoalScorers, getTeamGoals, TopGoals, FairPlayStandings, MatchMostGoals, getMatchMostGoals, OverallTeamStandings, getBestTeamStandings, getWorstTeamStandings, getThirdPlaceStandings, getFairPlayStandings, getRankedTeamStandings } from '../api/tournamentStatsApi';
import Table, { TableColumn } from '../components/Table';
import { GroupStanding } from '../api/groupsApi';

const TournamentStats: React.FC = () => {
  const [topGoalScorers, setTopGoalScorers] = useState<TopGoals[]>([]);
  const [teamGoals, setTeamGoals] = useState<TopGoals[]>([]);
  const [matchMostGoals, setMatchMostGoals] = useState<MatchMostGoals[]>([]);
  const [bestTeamStandings, setBestTeamStandings] = useState<OverallTeamStandings[]>([]);
  const [worstTeamStandings, setWorstTeamStandings] = useState<OverallTeamStandings[]>([]);
  const [thirdPlaceStandings, setThirdPlaceStandings] = useState<OverallTeamStandings[]>([]);
  const [fairPlayStandings, setFairPlayStandings] = useState<FairPlayStandings[]>([]);
  const [rankedTeamStandings, setRankedTeamStandings] = useState<OverallTeamStandings[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    fetchTeamGoals();
    fetchTopGoalScorers();
    fetchMatchMostGoals();
    fetchBestTeamStandings();
    fetchWorstTeamStandings();
    fetchThirdPlaceStandings();
    // fetchFairPlayStandings();
    fetchRankedTeamStandings();
  }, []);

  const fetchTopGoalScorers = async (): Promise<void> => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getTopGoalScorers();
      setTopGoalScorers(data);
    } catch (err) {
      console.error('Error fetching top goal scorers:', err);
      setError('Failed to load tournament statistics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTeamGoals = async (): Promise<void> => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getTeamGoals();
      setTeamGoals(data);
    } catch (err) {
      console.error('Error fetching team goals:', err);
      setError('Failed to load tournament statistics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMatchMostGoals = async (): Promise<void> => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getMatchMostGoals();
      setMatchMostGoals(data);
    } catch (err) {
      console.error('Error fetching match goals:', err);
      setError('Failed to load tournament statistics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBestTeamStandings = async (): Promise<void> => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getBestTeamStandings();
      setBestTeamStandings(data);
    } catch (err) {
      console.error('Error fetching best team standings:', err);
      setError('Failed to load tournament statistics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWorstTeamStandings = async (): Promise<void> => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getWorstTeamStandings();
      setWorstTeamStandings(data);
    } catch (err) {
      console.error('Error fetching worst team standings:', err);
      setError('Failed to load tournament statistics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchThirdPlaceStandings = async (): Promise<void> => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getThirdPlaceStandings();
      setThirdPlaceStandings(data);
    } catch (err) {
      console.error('Error fetching third place standings:', err);
      setError('Failed to load tournament statistics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFairPlayStandings = async (): Promise<void> => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getFairPlayStandings();
      setFairPlayStandings(data);
    } catch (err) {
      console.error('Error fetching fair play standings:', err);
      setError('Failed to load tournament statistics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRankedTeamStandings = async (): Promise<void> => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getRankedTeamStandings();
      setRankedTeamStandings(data);
    } catch (err) {
      console.error('Error fetching ranked team standings:', err);
      setError('Failed to load tournament statistics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const topScorerColumns: TableColumn<TopGoals>[] = [
    {
      key: 'player_name',
      title: 'שחקן',
      render: (item) => <span className="font-medium">{item.name}</span>
    },
    {
      key: 'goals',
      title: 'שערים',
      render: (item) => <span>{item.goals}</span>
    }
  ];

  const matchMostGoalsColumns: TableColumn<MatchMostGoals>[] = [
    {
        key: 'match',
        title: 'משחק',
        render: (item) => <span className="font-medium">{item.match}</span>
    },
    {
        key: 'score',
        title: 'תוצאה',
        render: (item) => <span className="font-medium">{item.score}</span>
    },
    {
        key: 'total_goals',
        title: 'גולים',
        render: (item) => <span className="font-medium">{item.total_goals}</span>
    }
  ];

  const teamGoalsColumns: TableColumn<TopGoals>[] = [
    { 
      key: 'logo_url', 
      title: '', 
      // Set a width that accommodates the h-8 (32px) logo and some padding.
      width: '65px', 
      render: (item) => (
        <img 
          src={item.logo_url} 
          alt={item.name} 
          // Use h-8 w-8 for size and rounded-full for the circle shape
          // ✨ ADDED flex-shrink-0 to prevent the image from being compressed
          className="h-8 w-8 rounded-full flex-shrink-0 object-cover" 
        />
      )
    },
    {
      key: 'name',
      title: 'קבוצה',
      render: (item) => <span className="font-medium">{item.name}</span>
    },
    {
      key: 'goals',
      title: 'שערים',
      render: (item) => <span>{item.goals}</span>
    }
  ];

  const standingsColumns: TableColumn<OverallTeamStandings>[] = [
    { 
        key: 'logo_url', 
        title: '', 
        // Set a width that accommodates the h-8 (32px) logo and some padding.
        width: '65px', 
        render: (item) => (
          <img 
            src={item.logo_url} 
            alt={item.team_name} 
            // Use h-8 w-8 for size and rounded-full for the circle shape
            // ✨ ADDED flex-shrink-0 to prevent the image from being compressed
            className="h-8 w-8 rounded-full flex-shrink-0 object-cover" 
          />
        )
      },
    { key: 'team_name', title: 'קבוצה', render: (item) => item.team_name},
    { key: 'matches_played', title: 'מש', render: (item) => item.matches_played},
    { key: 'wins', title: 'נ', render: (item) => item.wins },
    { key: 'draws', title: 'ת', render: (item) => item.draws},
    { key: 'losses', title: 'ה', render: (item) => item.losses},
    { key: 'goals_for', title: 'ז', render: (item) => item.goals_for},
    { key: 'goals_against', title: 'נ', render: (item) => item.goals_against },
    { key: 'goal_difference', title: '+/-', render: (item) => item.goal_difference },
    { key: 'points', title: 'נק', render: (item) => <span className="font-bold text-green-400">{item.points}</span> },
  ];

  const fairPlayColumns: TableColumn<FairPlayStandings>[] = [
    { 
        key: 'logo_url', 
        title: '', 
        // Set a width that accommodates the h-8 (32px) logo and some padding.
        width: '65px', 
        render: (item) => (
          <img 
            src={item.logo_url} 
            alt={item.team_name} 
            // Use h-8 w-8 for size and rounded-full for the circle shape
            // ✨ ADDED flex-shrink-0 to prevent the image from being compressed
            className="h-8 w-8 rounded-full flex-shrink-0 object-cover" 
          />
        )
      },
    { key: 'team_name', title: 'קבוצה', render: (item) => item.team_name},
    { key: 'yellow_cards', title: 'צהוב', render: (item) => item.yellow_cards},
    { key: 'second_yellow_cards', title: 'צהוב שני', render: (item) => item.second_yellow_cards },
    { key: 'red_cards', title: 'אדום', render: (item) => item.red_cards},
    { key: 'fair_play_points', title: 'נק', render: (item) => <span className="font-bold text-green-400">{item.fair_play_points}</span> },
  ];

  const TopGoalsKeyExtractor = (item: TopGoals): string => {
    return item.name;
  };

  const MatchMostGoalsKeyExtractor = (item: MatchMostGoals): string => {
    return item.match;
  };

  const TeamStandingsKeyExtractor = (item: OverallTeamStandings): string => {
    return item.team_name;
  };

  const FairPlayStandingsKeyExtractor = (item: FairPlayStandings): string => {
    return item.team_name;
  };

  const highlightCondition = (item: any): boolean => {
    return item.user_bet;
  };

  const MatchMostGoalsHighlightCondition = (item: MatchMostGoals): boolean => {
    return item.user_bet;
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">טוען סטטיסטיקות...</div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="text-red-500">{error}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div>
        <h1 className="text-2xl font-bold text-white mb-6">סטטיסטיקות של הטורניר</h1>
        
        <div className="flex flex-wrap gap-6 mb-8 justify-center lg:justify-start">
          <div className="w-full lg:w-auto">
            <Table
              className="w-full sm:w-80"
              title="קבוצה עם הכי הרבה שערים"
              withRanks={true}
              data={teamGoals}
              columns={teamGoalsColumns}
              keyExtractor={TopGoalsKeyExtractor}
              highlightCondition={highlightCondition}
              qualifyingPositions={1}
              emptyMessage="אין נתונים זמינים"
              collapsible={true}
              defaultCollapsed={false}
              collapsedShowHighlightedOnly={true}
              rowLimit={1}
              compact
            />
          </div>
          <div className="w-full lg:w-auto">
            <Table
              className="w-full sm:w-80"
              title="מלך השערים"
              withRanks={true}
              data={topGoalScorers}
              columns={topScorerColumns}
              keyExtractor={TopGoalsKeyExtractor}
              highlightCondition={highlightCondition}
              qualifyingPositions={1}
              emptyMessage="אין נתונים זמינים"
              collapsible={true}
              defaultCollapsed={false}
              collapsedShowHighlightedOnly={true}
              rowLimit={1}
              compact
            />
          </div>
          <div className="w-full lg:w-auto">
            <Table
              className="w-full sm:w-80"
              title="משחק עם הכי הרבה גולים "
              withRanks={true}
              data={matchMostGoals}
              columns={matchMostGoalsColumns}
              keyExtractor={MatchMostGoalsKeyExtractor}
              highlightCondition={MatchMostGoalsHighlightCondition}
              qualifyingPositions={1}
              emptyMessage="אין נתונים זמינים"
              collapsible={true}
              defaultCollapsed={false}
              collapsedShowHighlightedOnly={true}
              rowLimit={1}
              compact
            />
          </div>
          <div className="w-full lg:w-auto">
            <Table
              className="w-full sm:w-80"
              title="קבוצה הכי טובה"
              withRanks={true}
              data={bestTeamStandings}
              columns={standingsColumns}
              keyExtractor={TeamStandingsKeyExtractor}
              highlightCondition={highlightCondition}
              qualifyingPositions={1}
              emptyMessage="אין נתונים זמינים"
              collapsible={true}
              defaultCollapsed={false}
              collapsedShowHighlightedOnly={true}
              rowLimit={1}
              compact
            />
          </div>
          <div className="w-full lg:w-auto">
            <Table
              className="w-full sm:w-80"
              title="קבוצה הכי גרועה"
              withRanks={true}
              data={worstTeamStandings}
              columns={standingsColumns}
              keyExtractor={TeamStandingsKeyExtractor}
              highlightCondition={highlightCondition}
              qualifyingPositions={1}
              emptyMessage="אין נתונים זמינים"
              collapsible={true}
              defaultCollapsed={false}
              collapsedShowHighlightedOnly={true}
              rowLimit={1}
              compact
            />
          </div>
          {/* <div className="dark:bg-gray-800 rounded-lg shadow-md overflow-hidden w-fit min-w-80">
            <Table
              className="w-full sm:w-80"
              title="הקבוצה הכי לא הוגנת"
              withRanks={true}
              data={fairPlayStandings}
              columns={fairPlayColumns}
              keyExtractor={FairPlayStandingsKeyExtractor}
              highlightCondition={highlightCondition}
              qualifyingPositions={1}
              emptyMessage="אין נתונים זמינים"
              collapsible={true}
              defaultCollapsed={false}
              collapsedShowHighlightedOnly={true}
              compact
            />
          </div> */}
          <div className="w-full lg:w-auto">
            <Table
              className="w-full sm:w-80"
              title="מקומות שלישי"
              withRanks={true}
              data={thirdPlaceStandings}
              columns={standingsColumns}
              keyExtractor={TeamStandingsKeyExtractor}
              highlightCondition={highlightCondition}
              qualifyingPositions={8}
              emptyMessage="אין נתונים זמינים"
              collapsible={true}
              defaultCollapsed={false}
              collapsedShowHighlightedOnly={false}
              rowLimit={1}
              compact
            />
          </div>
          {rankedTeamStandings.length > 0 && (
            <div className="w-full lg:w-auto">
              <Table
              className="w-full sm:w-80"
                title="הסינדרלות"
                withRanks={true}
                data={rankedTeamStandings}
                columns={standingsColumns}
                keyExtractor={TeamStandingsKeyExtractor}
                highlightCondition={highlightCondition}
                qualifyingPositions={1}
                emptyMessage="אין נתונים זמינים"
                collapsible={true}
                defaultCollapsed={false}
                collapsedShowHighlightedOnly={true}
                rowLimit={1}
                compact
              />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default TournamentStats;

